/**
 * propertyExpression.js
 *
 * TRANSITIONAL / LEGACY — see docs/Binding_Architecture_FINAL.md §5
 * ("Legacy Generator"). buildPropertyExpression() builds the Jinja2
 * expression for the legacy `hass_config.properties` row shape only
 * (pre-AutomationBinding projects). It is not part of the current
 * strategy-driven generator architecture — that lives in
 * bindingGenerators.js, which every AutomationBinding-backed property
 * goes through instead (see HomeAssistantGenerator._appendBindingProperties).
 *
 * Still called by:
 *   - HomeAssistantGenerator._appendProperties(), at export time, for any
 *     widget whose hass_config.properties rows haven't been converted to
 *     AutomationBinding instances
 *   - migration/LegacyEventsToAst.js, when migrating that same row shape
 *
 * This file stays in place, unrenamed, until legacy (pre-AutomationBinding)
 * export support is no longer required — at which point it is removed in
 * its own change.
 *
 * @param {object} prop — legacy property row shape:
 *   { property, entityId, attribute, text (filter), suffix, fallback,
 *     on_state, off_state }
 * @param {string} objType — LVGL object type (e.g. 'led', 'label')
 * @returns {string} Jinja2 expression
 */
export function buildPropertyExpression(prop, objType) {
  const entityRef = `"${prop.entityId}"`;
  const filter    = prop.text     ? ` ${prop.text}`      : '';
  const suffix    = prop.suffix   ? ` ~ "${prop.suffix}"` : '';
  const fallback  = prop.fallback ?? '';
  const attr      = prop.attribute;

  // RGB colour attribute — multi-line hex conversion
  if (attr === 'rgb_color') {
    return (
      `{% set rgb = state_attr(${entityRef}, "rgb_color") %}` +
      `{{ "#%02x%02x%02x" | format(rgb[0], rgb[1], rgb[2]) if rgb else "#ffffff" }}`
    );
  }

  // Specific state attribute (brightness, current_position, …)
  if (attr) {
    if (fallback !== '') {
      return (
        `{{ state_attr(${entityRef}, "${attr}")${filter}${suffix}` +
        ` if state_attr(${entityRef}, "${attr}") is not none else "${fallback}" }}`
      );
    }
    return `{{ state_attr(${entityRef}, "${attr}")${filter}${suffix} }}`;
  }

  // Colour property — on/off colour toggle
  if (prop.property.includes('color')) {
    const on  = prop.on_state  || '#ffffff';
    const off = prop.off_state || '#888888';
    return `{{ "${on}" if is_state(${entityRef}, "on") else "${off}" }}`;
  }

  // LED brightness — implicit 255/0 toggle when no explicit on/off is provided
  if (objType === 'led' && prop.property === 'val' && !prop.on_state && !prop.off_state) {
    return `{{ 255 if is_state(${entityRef}, "on") else 0 }}`;
  }

  // Explicit numeric/string on/off values
  if (
    prop.on_state !== undefined && prop.off_state !== undefined &&
    prop.on_state !== ''        && prop.off_state !== ''
  ) {
    return `{{ "${prop.on_state}" if is_state(${entityRef}, "on") else "${prop.off_state}" }}`;
  }

  // Entity state with optional filter; guarded by has_value() when fallback is set
  if (fallback !== '') {
    return `{{ states(${entityRef})${filter}${suffix} if has_value(${entityRef}) else "${fallback}" }}`;
  }
  return `{{ states(${entityRef})${filter}${suffix} }}`;
}
